import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "The birth of LISP - a summary of John McCarthy's original paper",
  "description": "",
  "published": "2012-08-22T00:00:00.000Z",
  "redirect_from": ["/blog/the-birth-of-lisp-a-summary-of-john-mccarthys-original-paper/swizec/5075"],
  "categories": "Artificial intelligence, John McCarthy, Lambda calculus, Language, Lisp, McCarthy, NIL, Uncategorized",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <blockquote>
      <p parentName="blockquote">{`A programming system called `}<a parentName="p" {...{
          "href": "http://en.wikipedia.org/wiki/Lisp_%28programming_language%29",
          "title": "Lisp (programming language)"
        }}>{`LISP`}</a>{` (for LISt Processor) has been developed for the IBM 704 computer by the `}<a parentName="p" {...{
          "href": "http://en.wikipedia.org/wiki/Artificial_intelligence",
          "title": "Artificial intelligence"
        }}>{`Artificial Intelligence`}</a>{` group at M.I.T. The system was designed to facilitate experiments with a proposed system called the Advice Taker, whereby a machine /../ could exhibit “common sense” in carrying out its instructions.`}</p>
      <p parentName="blockquote">{`/../`}</p>
      <p parentName="blockquote">{`LISP eventually came to be based on a scheme for representing the `}<a parentName="p" {...{
          "href": "http://en.wikipedia.org/wiki/Computable_function",
          "title": "Computable function"
        }}>{`partial recursive functions`}</a>{` of a certain class of symbolic expressions. It now seems expedient to expound the system by starting with the class of expressions called S-expressions and the functions called S-functions.`}</p>
    </blockquote>
    <p>{`With that, `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/John_McCarthy_%28computer_scientist%29",
        "title": "John McCarthy (computer scientist)"
      }}>{`John McCarthy`}</a>{` starts off his `}<a parentName="p" {...{
        "href": "https://docs.google.com/viewer?url=http%3A%2F%2Fwww.cs.berkeley.edu%2F~christos%2Fclassics%2Flisp.ps"
      }}>{`Recursive Functions of Symbolic Expressions and Their Computation by Machine`}</a>{` paper. Two years after LISP was originally developed, long before many of the concepts presented in the paper become the norm.`}</p>
    <p>{`Garbage collection, for instance, was first proposed/described in this paper (invented in 1959, paper published in 1960).`}</p>
    <p>{`While LISP never really delivered on its promise of artificial intelligence and the whole field has started turning in a `}<a parentName="p" {...{
        "href": "http://www.tor.com/blogs/2011/06/norvig-vs-chomsky-and-the-fight-for-the-future-of-ai"
      }}>{`probabilistic rather than descriptive approach`}</a>{`, this paper is a good read for anyone whose interests in computer science and engineering go beyond comparing Ruby to PHP on a syntactical basis.`}</p>
    <p>{`The Paper is 34 pages long and took me roughly 6 hours to grok, here's the gist in ~1300 words. For details, read the original :)`}</p>
    <h2 {...{
      "id": "functions-and-function-definitions"
    }}>{`Functions and function definitions`}</h2>
    <p>{`For starters we need some core mathematical constructs. These are well known, but the conditional expression was believed to be new at the time.`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li"><em parentName="p"><a parentName="em" {...{
              "href": "http://en.wikipedia.org/wiki/Partial_function",
              "title": "Partial function"
            }}>{`Partial Functions`}</a></em>{` - functions only defined on parts of their domain, they arise because the computation is intractable for some values (turing completeness)`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><em parentName="p">{`Propositional Expressions and Predicates`}</em>{` - functions/operators returning a `}<em parentName="p">{`true`}</em>{` or a `}<em parentName="p">{`false`}</em>{` (and, or, not, etc.)`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><em parentName="p">{`Conditional Expressions`}</em>{` - a notation for expressing values depending on predicates, which was traditionally done using human words. For instance, definining the function `}<em parentName="p">{`abs(x)`}</em>{` benefits from this concept. Best explained with an example:`}</p>
        <pre parentName="li"><code parentName="pre" {...{
            "className": "language-math"
          }}>{`(1 < 2→4, 1 > 2→3) = 4
(2 < 1→4, 2 > 1→3, 2 > 1→2) = 3
(2 < 1→4,T →3) = 3
(2 < 1→ 0 0,T →3) = 3
(2 < 1→3,T → 0 0) is undefined
(2 < 1→3, 4 < 1→4) is undefined
`}</code></pre>
      </li>
      <li parentName="ul">
        <p parentName="li"><em parentName="p">{`Recursive Function Definitions`}</em>{` - because of the way `}{`_`}{`conditional expression_s are defined, we can use them recursively without falling into infinite loops:`}</p>
        <pre parentName="li"><code parentName="pre" {...{
            "className": "language-math"
          }}>{`n! = (n = 0→1,T →n · (n−1)!)

2! = (2 = 0→1,T →2 · (2−1)!)
   = 2 · 1! = 2 · (1 = 0→1T →·(1−1)!)
   = 2 · 1 · 0!
   = 2 · 1 · (0 = 0→1,T →0 · (0−1)!)
   = 2 · 1 · 1
   = 2
`}</code></pre>
        <p parentName="li">{`The paper also defines propositional operators as recursive functions, because this helps us handle intractability without messing everything up.`}</p>
        <pre parentName="li"><code parentName="pre" {...{
            "className": "language-math"
          }}>{`p ∧ q = (p→q,T →F)
p ∨ q = (p→T,T →q)
¬p = (p→F,T →T)
p ⊃ q = (p→q,T →T)
`}</code></pre>
      </li>
      <li parentName="ul">
        <p parentName="li"><em parentName="p">{`Functions and Forms`}</em>{` - the paper makes a distinction between forms - `}<em parentName="p">{`y^2+x -`}</em>{` and functions - `}<em parentName="p">{`f(x) = ...`}</em>{` - because we need a clean notation for applying functions to parameters. Thus the notion of a `}<em parentName="p">{`lambda`}</em>{` is introduced that takes arguments, binds them to an expression and returns the result:`}</p>
        <pre parentName="li"><code parentName="pre" {...{
            "className": "language-math"
          }}>{`λ((x, y), y^2 + x),
λ((u, v), v^2 + u)
and λ((y, x), x^2 +y)
are the same function
`}</code></pre>
      </li>
      <li parentName="ul">
        <p parentName="li"><em parentName="p">{`Expressions and Recursive Functions`}</em>{` - lambdas aren't good enough for defining things recursively because you can't have an operator as `}<em parentName="p">{`"the whole expression"`}</em>{`. To fix this a `}<em parentName="p">{`label`}</em>{` is introduced that binds the lambda expression to a name, so it can be reused:`}</p>
        <pre parentName="li"><code parentName="pre" {...{
            "className": "language-math"
          }}>{`label(sqrt, λ((a, x, eps), (|x2 −a| < eps→x,T →sqrt(a, 1/2(x+ a/x), eps))))
`}</code></pre>
      </li>
    </ul>
    <h2 {...{
      "id": "recursive-functions-of-symbolic-expressions"
    }}>{`Recursive functions of Symbolic expressions`}</h2>
    <p>{`Now that we have a solid basis, it's time to define symbolic expressions (S-expressions for short), show how they can be used to define functions, how those functions can in turn be represented as S-expressions and finally we will define the all important `}<em parentName="p">{`apply`}</em>{` function, which lets the computer calculate anything.`}</p>
    <p><strong parentName="p">{`S-expressions`}</strong>{` are formed by using the special characters `}<em parentName="p">{`dot, left-, right- parentheses`}</em>{` `}{`[`}<em parentName="p">{`·`}</em>{`()] and distinguishable atomic symbols (defined in this paper as capital Latin letters, digits and a single space).`}</p>
    <ol>
      <li parentName="ol">{`Atomic symbols are S-expressions`}</li>
      <li parentName="ol">{`If `}<em parentName="li">{`e1`}</em>{` and `}<em parentName="li">{`e2`}</em>{` are S-expressions, so is `}<em parentName="li">{`(e1 · e2)`}</em></li>
    </ol>
    <p>{`So an S-expression is any ordered pair of S-expressions. Strictly speaking we are supposed to terminate any nesting of S-expressions with a `}<em parentName="p">{`[NIL]`}{`(`}<a parentName="em" {...{
          "href": "http://en.wikipedia.org/wiki/NIL"
        }}>{`http://en.wikipedia.org/wiki/NIL`}</a></em>{`%28programming`}<em parentName="p">{`language%29 "NIL (programming language)")`}</em>{`, but we agree that `}<em parentName="p">{`(m)`}</em>{` stands for `}<em parentName="p">{`(m`}</em>{` · NIL) `}{`_`}{`_`}{`and that instead of a dot operator we can write expressions as lists, which makes our lives a lot easier`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-math"
      }}>{`((AB,C),D) for ((AB · (C ·NIL)) · (D ·NIL))
((A,B),C,D·E) for ((A· (B ·NIL)) · (C · (D · E)))
`}</code></pre>
    <p><strong parentName="p">{`Functions of S-expressions`}</strong>{` use conventional notation, except they're limited to lower case letters, brackets and semicolons to distinguish them from S-expressions. These are called M-expressions (meta-expressions)`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-math"
      }}>{`car[x]
car[cons[(A· B); x]]
`}</code></pre>
    <p>{`McCarthy then defines five`}<strong parentName="p">{` Elementary S-functions and Predicates`}</strong>{`. `}<em parentName="p">{`atom`}</em>{` tells us whether something is atomic, `}<em parentName="p">{`eq`}</em>{` compares two atomic things and tells us if they're equal, `}<em parentName="p">{`car`}</em>{` returns the left part of an S-expression, `}<em parentName="p">{`cdr`}</em>{` returns the right and `}<em parentName="p">{`cons`}</em>{` constructs an S-expression from two atoms.`}{`\\`}</p>
    <p>{`By introducing `}<strong parentName="p">{`Recursive S-functions`}</strong>{` we finally get a strong enough class of functions that we can construct all computable functions. McCarthy now defines a bunch of useful functions`}<em parentName="p">{` - `}{`_`}{`let's look at the example of `}{`_`}{`ff`}</em>{`, which returns the first element of an S-expression.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-math"
      }}>{`ff[x] = [atom[x]→x;T →ff[car[x]]]

ff [((A · B) · C)] = A
calculation:
= [atom[((A · B) · C)]→((A· B) · C);T →ff[car[((A· B)C·)]]]
= [F →((A· B) · C);T →ff[car[((A· B) · C)]]]
= [T →ff[car[((A· B) · C)]]]
= ff[car[((A· B) · C)]]
= ff[(A· B)] = [atom[(A · B)]→(A· B);T →ff[car[(A ·B)]]]
= [F →(A· B);T →ff[car[(A· B)]]]
= [T →ff[car[(A· B)]]]
= ff[car[(A· B)]]
= ff[A]
= [atom[A]→A;T →ff[car[A]]]
= [T →A;T →ff[car[A]]]
= A
`}</code></pre>
    <p>{`The next thing we need is `}<strong parentName="p">{`Representing S-functions as S-expressions. `}</strong>{`We do this simply by changing lowercase into uppercase and properly handling some cases around labels and quotes. The M-expression for subst`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-math"
      }}>{`label [subst; λ [[x; y; z]; [atom [z] → [eq [y; z] → x; T → z]; T → cons [subst [x; y; car [z]]; subst [x; y; cdr [z]]]]]]
`}</code></pre>
    <p>{`becomes the S-expression`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-math"
      }}>{`(LABEL, SUBST, (LAMBDA, (X, Y, Z), (COND ((ATOM, Z), (COND, (EQ, Y, Z), X), ((QUOTE, T), Z))), ((QUOTE, T), (CONS, (SUBST, X, Y, (CAR Z)), (SUBST, X, Y, (CDR, Z)))))))
`}</code></pre>
    <p>{`Soup. It looks like a soup of weird.`}</p>
    <p>{`But it's very simple in principle. Add some tactical line breaks and code alignment and you get readable code!`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-math"
      }}>{`(LABEL, SUBST,
        (LAMBDA, (X, Y, Z),
                 (COND ((ATOM, Z),
                        (COND, (EQ, Y, Z), X),
                        ((QUOTE, T), Z))),
                 ((QUOTE, T),
                  (CONS, (SUBST, X, Y, (CAR Z)),
                         (SUBST, X, Y, (CDR, Z)))))))
`}</code></pre>
    <p><strong parentName="p">{`The Universal S-Function `}<em parentName="strong">{`apply`}</em></strong>{` is the all important function that gives us a way of applying a function to a list of arguments. It's a bit tricky to define and reason about - all you have to keep in mind is that it behaves exactly the same as the function it is applying. The main consequence of this is that we can now implement a machine (an interpreter) that only needs to implement the `}<em parentName="p">{`apply`}</em>{` function and it can run any program we write.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-math"
      }}>{`λ[[x; y]; cons[car[x]; y]][(A,B); (C,D)]
= apply[(LAMBDA, (X,Y ), (CONS, (CAR,X),Y )); ((A,B), (C,D))] = (A,C,D)
`}</code></pre>
    <p>{`In the background, `}<em parentName="p">{`apply`}</em>{` is an expression that represents the value of the function applied to arguments, while `}<em parentName="p">{`eval`}</em>{` does the heavy lifting of calculation, which takes an expression and its "name" as arguments, so it can attach the value to something useful in the next step of calculation.`}</p>
    <p>{`And we've come at the juicy goodness of functional programming - `}<strong parentName="p">{`Functions with Functions as Arguments. `}</strong>{`The idea is that we can use an S-expression as an argument for another S-expression similar to what we did with `}<em parentName="p">{`apply`}</em>{`, except in a more generally useful way.`}</p>
    <p>{`The paramount example is `}<em parentName="p">{`maplist `}</em>{`(called "map" these days), which applies a function to every member of a list to construct a different list - for instance increasing all values by +1.`}</p>
    <h2 {...{
      "id": "the-lisp-programming-system"
    }}>{`The LISP programming system`}</h2>
    <p>{`LISP's core is a system of translating S-expressions into list structures that can be evaluated by a computer. This system was intended for various uses, the ultimate of which was the Advice Taker system.`}</p>
    <p><strong parentName="p">{`Representing S-Expressions by a List Structure`}</strong>{` can be visualised with a set of connected boxes. Each box is divided into two parts - the right being the `}<em parentName="p">{`address field`}</em>{` and the left being the `}<em parentName="p">{`decrement field`}</em>{`. They contain the locations for subexpressions returned by `}<em parentName="p">{`car`}</em>{` and `}<em parentName="p">{`cdr`}</em>{` respectively.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2012/08/list-structure.png",
        "alt": "List structure",
        "title": "List structure"
      }}></img></p>
    <p>{`Substructures can be repeated, but cycles aren't allowed - the bottom-right example wouldn't fly. When subexpressions are repeated their in-memory representation depends mostly on the program's history, but luckily this doesn't affect the final result.`}</p>
    <p>{`The reason McCarthy prohibits circular structures is interesting - they might help with representing, say, recursive functions, but there are too many difficulties with printing them in a world with our topology.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2012/08/example-list-structure.png",
        "alt": "((A · B) · (C · (E · F)))",
        "title": "((A · B) · (C · (E · F)))"
      }}></img></p>
    <p>{`When storing atomic symbols an `}<strong parentName="p">{`association list`}</strong>{` is used. These are used for anything from print names, to memory locations of values - the machine uses them for whatever low-level concept it needs to make the mathematical principles of LISP work.`}</p>
    <p>{`For example, to store the word `}<em parentName="p">{`DIFFERENTIATE`}</em>{` in an association list on a computer with 6-bit words a structure such as this is used:`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2012/08/association-list.png",
        "alt": "Association list for \"DIFFERENTIATE\"",
        "title": "Association list for \"DIFFERENTIATE\""
      }}></img></p>
    <p>{`Even back then computers had more memory than a typical program would be using at all times, so a concept of a `}<strong parentName="p">{`free storage list`}</strong>{` was introduced. At its core, the free storage list is simply a list structure that catalogs all the available free memory and starts with a pointer called `}<em parentName="p">{`FREE`}</em>{`.`}</p>
    <p>{`A straightforward concept no doubt, but it introduces the invention of `}<strong parentName="p">{`garbage collection`}</strong>{`. Whenever a subexpression is no longer needed it can be added to the free storage list. And because we're using lists, every single redeemed element is useful!`}</p>
    <p>{`Sweet!`}</p>
    <p>{`When our program runs out of free memory the garbage collector (called `}{`_`}{`"a reclamation cycle" `}{`_`}{`back then) goes through all accessible registers and marks them. Then it simply goes through the whole memory and anything that wasn't marked as accessible gets added to the free storage list.`}</p>
    <p>{`It's easy to imagine how the `}<strong parentName="p">{`elementary S-functions work in the Computer`}</strong><em parentName="p">{`. `}{`_`}{`They amount to nothing more than returning the correct part of the word. So `}{`_`}{`car`}</em>{` returns the address value, `}<em parentName="p">{`cdr`}</em>{` the decrement value, `}<em parentName="p">{`cons`}</em>{` takes from free storage and fills the values ... and so on.`}</p>
    <p>{`Everything else is merely constructed from elementary functions.`}</p>
    <p>{`Another interesting observation is that talking about `}<strong parentName="p">{`representation of S-functions by Programs`}</strong>{` McCarthy ever so casually introduces lazy evaluation by saying that compilation is simple and `}<em parentName="p">{`"conditional expressions must be so compiled that only the `}{`_`}{`p's`}</em>{` and `}<em parentName="p">{`e's`}</em>{` that are required are computed"`}{`_`}</p>
    <p>{`There are some problems with compiling recursive functions because of a possibility of register collisions - needing a part of memory that is also used by the previous level of recursion - which is solved by employing a mechanism we now know as `}<em parentName="p">{`The Stack`}</em>{`.`}</p>
    <h2 {...{
      "id": "fin"
    }}>{`Fin`}</h2>
    <p>{`Further on McCarthy details the current state of the LISP system - everything basically works, a manual is being prepared, the compiler is 60-times faster than the interpreter and floating point operations are slow.`}</p>
    <p>{`There is also a short chapter on an alternative presentation of functions of symbolic expressions called `}<em parentName="p">{`L-expressions`}</em>{`. They are the basis of linear LISP and use the more familiar elementary functions `}<em parentName="p">{`first, rest`}</em>{` and `}<em parentName="p">{`combine`}</em>{`.`}</p>
    <p>{`Mathematically speaking linear LISP includes LISP, but for computers regular LISP is said to be faster.`}</p>
    <p>{`Now if only there weren't so many damn parentheses ... `}<a parentName="p" {...{
        "href": "http://swizec.com/blog/tag/clojure"
      }}>{`I tried to learn LISP (clojure really)`}</a>{`, I really did. But the syntax was just too jarring.`}</p>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://passionsjustlikemine.wordpress.com/2012/07/27/john-mccarthy-in-the-literary-arena/"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/102977749_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://passionsjustlikemine.wordpress.com/2012/07/27/john-mccarthy-in-the-literary-arena/"
        }}>{`John McCarthy in the Literary Arena`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://blog.fogus.me/2012/07/25/some-lisp-books-and-then-some/"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/noimg_97_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://blog.fogus.me/2012/07/25/some-lisp-books-and-then-some/"
        }}>{`Some Lisp books (and then some)`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://nic.ferrier.me.uk/blog/2012_07/tips-and-tricks-for-emacslisp"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/noimg_3_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://nic.ferrier.me.uk/blog/2012_07/tips-and-tricks-for-emacslisp"
        }}>{`Tips and tricks with emacslisp for fun and profit`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.drdobbs.com/tools/developers-reading-list/240003875"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/101202848_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://www.drdobbs.com/tools/developers-reading-list/240003875"
        }}>{`Developer's Reading List`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://kresimirbojcic.com/2012/08/14/why-lisp-did-not-and-never-will-gain-enough-traction.html"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/noimg_46_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://kresimirbojcic.com/2012/08/14/why-lisp-did-not-and-never-will-gain-enough-traction.html"
        }}>{`Why Lisp Did Not and Never Will Gain Enough Traction`}</a></li>
    </ul>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/?px",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=27a245ba-8da2-4b42-a319-61686149e5a2",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      